import React, { useState } from 'react'
import { Search } from 'lucide-react'
import { useDraggable } from '@dnd-kit/core'
import { BLOCK_CATALOG, BLOCK_CATEGORIES } from './blocks/block-catalog'
import type { BlockConfig, BlockType } from '../../types'

const PaletteItem = React.memo(function PaletteItem({ config, onClickAdd }: { config: BlockConfig; onClickAdd: (type: BlockType) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${config.type}`,
    data: { fromPalette: true, type: config.type },
  })

  return (
    <div>
      <div
        ref={setNodeRef}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-default-200 bg-content1 hover:bg-primary/10 hover:border-primary/30 transition-colors text-left cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-50' : ''}`}
        {...listeners}
        {...attributes}
      >
        <div>
          <p className="text-sm font-medium text-default-600">{config.label}</p>
          <p className="text-xs text-default-400">{config.description}</p>
        </div>
      </div>
      <button
        className="w-full mt-0.5 text-xs text-default-400 hover:text-primary text-center py-0.5"
        onClick={() => onClickAdd(config.type)}
      >
        + click to add
      </button>
    </div>
  )
})

interface ComponentLibraryProps {
  onAddBlock: (type: BlockType) => void
}

export function ComponentLibrary({ onAddBlock }: ComponentLibraryProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  const filteredBlocks = BLOCK_CATALOG.filter((config) => {
    if (searchQuery && !config.label.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !config.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false
    }
    if (activeCategory && config.category !== activeCategory) return false
    return true
  })

  return (
    <div className="w-56 border-r border-default-200 bg-content2 overflow-y-auto p-3 flex flex-col">
      <div className="relative mb-3">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-default-400" />
        <input
          type="text"
          placeholder="Search blocks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-7 pr-2 py-1.5 text-xs rounded-lg border border-default-200 bg-content1 focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        <button
          className={`px-2 py-0.5 rounded text-xs ${!activeCategory ? 'bg-primary text-white' : 'bg-default-100 text-default-600 hover:bg-default-200'}`}
          onClick={() => setActiveCategory(null)}
        >
          All
        </button>
        {BLOCK_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            className={`px-2 py-0.5 rounded text-xs ${activeCategory === cat.id ? 'bg-primary text-white' : 'bg-default-100 text-default-600 hover:bg-default-200'}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="space-y-2 flex-1 overflow-y-auto">
        {filteredBlocks.map((config) => (
          <PaletteItem
            key={config.type}
            config={config}
            onClickAdd={(type) => onAddBlock(type)}
          />
        ))}
      </div>
    </div>
  )
}
